/**
 * One-sided exercises, logged a side at a time.
 *
 * A single-arm row used to take one weight and one rep count, with a sentence
 * under the boxes saying "log what you did on a side". The data showed what
 * that cost: Cable Lateral Raise sets noted "Left 13, right 15", and whole
 * sessions logged as six sets noted R, L, R, L, R, L — the sides living in the
 * notes because the form had nowhere else to put them. (2026-09-15)
 *
 * A round is now two ordinary set rows — `side` 'L' and 'R' — sharing a
 * `set_index`. Rows rather than a second pair of columns on one row, so that
 * everything that reads a set (e1RM, PRs, tonnage, the progress charts) works on
 * each side unchanged: a record is your better side, and tonnage counts the
 * work both sides did.
 *
 * What does have to know is anything that COUNTS sets, because two rows are one
 * round. That is this file. And a round only counts toward a plan's "3 sets"
 * once BOTH sides are in — one arm of a set is half a set.
 *
 * Counting is decided by the rows, not by the exercise: everything logged before
 * sides existed has no side, and keeps counting one row as one set, which is
 * what it was.
 *
 * Dexie-free (type-only imports), so all of it is reachable from a test.
 */

import type { Exercise } from '../db/index.ts'

export type Side = 'L' | 'R'
export const SIDES: readonly Side[] = ['L', 'R']
export const SIDE_LABEL: Record<Side, string> = { L: 'Left', R: 'Right' }

/** The fields of a set row this file reads. */
export type Sided = {
  id: string
  session_id: string
  exercise_id: number
  set_index: number
  is_warmup: boolean
  side?: Side | null
}

export type Round<T extends Sided> = {
  key: string
  set_index: number
  is_warmup: boolean
  /** A set with no side: every two-sided exercise, and all history before sides. */
  single?: T
  left?: T
  right?: T
}

/**
 * Does this exercise get a pair of boxes per side?
 *
 * One-sided AND loaded. The per-side instruction only ever appeared under a
 * weight box, so that is the set of exercises that told people to log a side at
 * a time; a side plank or a split squat jump never did, and gets one row.
 */
export function logsSides(
  exercise: Pick<Exercise, 'is_unilateral'> | null | undefined,
  measures: readonly string[],
): boolean {
  return Boolean(exercise?.is_unilateral) && measures.includes('weight')
}

export function isSided(set: Pick<Sided, 'side'>): boolean {
  return set.side === 'L' || set.side === 'R'
}

/** Rows grouped into rounds, in the order each round was first logged. */
export function groupRounds<T extends Sided>(sets: readonly T[]): Round<T>[] {
  const out: Round<T>[] = []
  const open = new Map<string, Round<T>>()

  for (const s of sets) {
    if (!isSided(s)) {
      out.push({ key: s.id, set_index: s.set_index, is_warmup: s.is_warmup, single: s })
      continue
    }
    const base = `${s.session_id}|${s.exercise_id}|${s.set_index}`
    const slot = s.side === 'L' ? 'left' : 'right'
    let round = open.get(base)
    // A second row claiming a side that is already filled can only come from
    // two devices writing the same set. It is still a set somebody logged, so
    // it becomes its own (half) round rather than silently disappearing.
    if (!round || round[slot]) {
      round = { key: `${base}|${s.id}`, set_index: s.set_index, is_warmup: s.is_warmup }
      open.set(base, round)
      out.push(round)
    }
    round[slot] = s
  }
  return out
}

/** Both sides in, or a set that never had sides. */
export function isComplete<T extends Sided>(round: Round<T>): boolean {
  return Boolean(round.single || (round.left && round.right))
}

/**
 * How many sets, counting a round as one.
 *
 * `complete` is the plan's question — "have I done my 3 sets" — where half a
 * round is not a set yet. Without it this answers "how many sets are on the
 * screen", which is what a list header or a delete confirmation needs.
 */
export function countRounds<T extends Sided>(
  sets: readonly T[],
  opts: { working?: boolean; complete?: boolean } = {},
): number {
  return groupRounds(sets).filter((r) =>
    (!opts.working || !r.is_warmup) && (!opts.complete || isComplete(r)),
  ).length
}

export function rowsOf<T extends Sided>(round: Round<T>): T[] {
  return [round.single, round.left, round.right].filter((s): s is T => Boolean(s))
}

export function hasSides(sets: readonly Pick<Sided, 'side'>[]): boolean {
  return sets.some(isSided)
}

/** The most recent row for one side, or null. */
export function lastOfSide<T extends Sided>(sets: readonly T[], side: Side): T | null {
  for (let i = sets.length - 1; i >= 0; i--) {
    if (sets[i].side === side) return sets[i]
  }
  return null
}

/**
 * The set one side's boxes should carry from, for prefill.
 *
 * That side's last row when the rows have sides. When they don't — last
 * session was logged before sides existed, or on an older phone — the last row
 * stands for both sides, which is what it described.
 */
export function carryFor<T extends Sided>(sets: readonly T[] | null | undefined, side: Side): T | null {
  if (!sets?.length) return null
  return hasSides(sets) ? lastOfSide(sets, side) : sets[sets.length - 1]
}

/**
 * The `set_index` for a new round: one past the highest in use.
 *
 * Not a count of rows — a round is two of them — and not a count of rounds
 * either, which collides with a surviving set after one is removed.
 */
export function nextSetIndex(sets: readonly Pick<Sided, 'set_index'>[]): number {
  return sets.reduce((max, s) => Math.max(max, s.set_index), 0) + 1
}

/** "L 10 × 20 / R 8 × 20" for a round, or the set itself when it has no sides. */
export function formatRound<T extends Sided>(
  round: Round<T>,
  format: (set: T) => string,
): string {
  if (round.single) return format(round.single)
  return [
    round.left && `L ${format(round.left)}`,
    round.right && `R ${format(round.right)}`,
  ].filter(Boolean).join(' / ')
}
